// Bulk tier reclassify — fast-grade Justin's customer rolodex without
// drilling into each detail page. Route: /customers/bulk-tier (wired in App.tsx).
//
// Flow:
//   1. Load all customers via listCustomers()
//   2. Show as a scrollable table; each row carries a "new tier" picker
//      that defaults to the row's current tier
//   3. Per-row pending edit = pickedTier !== currentTier. Row highlights
//      and a "● modified" indicator appears.
//   4. "Set all visible to X" applies a tier to every currently-filtered
//      row as a pending edit (still requires Save to persist).
//   5. "Save N change(s)" loops the pending edits in batches of 10, calls
//      updateCustomer for each with { tier, tierManualOverride: true,
//      tierLastReviewed: nowIso }. Per-row status (queued / saving / saved
//      / error) is tracked in component state and shown inline.
//   6. After save: refresh from RTDB so the "current tier" column reflects
//      what's on the wire; clear pending edits + statuses.
//
// Batching: 10 in flight at a time via a simple sequential window over
// Promise.allSettled to be gentle on RTDB and avoid spamming /customers/{id}
// writes. (RTDB doesn't rate-limit small customer counts like KMF's, but
// this also keeps the UI from going all-spinner at once and makes errors
// recoverable per-row.)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCustomers, updateCustomer } from '@/shared/crm';
import type { Customer, CustomerTier } from '@/shared/crmTypes';
import { TierBadge } from '@/components/TierBadge';

type TierFilter = CustomerTier | 'all';

const TIER_FILTERS: Array<{ key: TierFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'vip', label: 'VIP' },
  { key: 'good', label: 'Good' },
  { key: 'slow_pay', label: 'Slow Pay' },
  { key: 'bad', label: 'Bad' },
  { key: 'new', label: 'New' },
];

const TIER_PICKER_OPTIONS: Array<{ key: CustomerTier; label: string }> = [
  { key: 'vip', label: 'VIP' },
  { key: 'good', label: 'Good' },
  { key: 'slow_pay', label: 'Slow Pay' },
  { key: 'bad', label: 'Bad' },
  { key: 'new', label: 'New' },
];

type RowStatus =
  | { kind: 'idle' }
  | { kind: 'queued' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

const SAVE_CONCURRENCY = 10;

function fmtRelativeDays(iso?: string): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'in the future';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function latestNoteText(c: Customer): string | null {
  if (!c.notes || c.notes.length === 0) return null;
  // Notes are appended; take the last one as the most recent.
  const last = c.notes[c.notes.length - 1];
  return last?.text ?? null;
}

export function CustomersBulkTierPage() {
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [tierFilter, setTierFilter] = useState<TierFilter>('all');

  // Pending edits — id -> picked tier. Absent when row hasn't been touched
  // OR when the user picked the same tier as current (we keep this in sync
  // when the pickers fire; a row in here always differs from the source).
  const [pending, setPending] = useState<Record<string, CustomerTier>>({});

  // Per-row save status — only set during/after a Save All run.
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  // Expanded notes (click-to-expand on the Notes column).
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});

  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await listCustomers();
      setCustomers(data);
    } catch (e) {
      setError(`Failed to load customers: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  // Debounce search input → searchQuery
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const filtered = useMemo(() => {
    let rows = customers;
    if (tierFilter !== 'all') {
      rows = rows.filter((c) => c.tier === tierFilter);
    }
    const needle = searchQuery.trim().toLowerCase();
    if (needle) {
      rows = rows.filter(
        (c) =>
          c.companyName.toLowerCase().includes(needle) ||
          (c.code ?? '').toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [customers, tierFilter, searchQuery]);

  const pendingCount = Object.keys(pending).length;

  function setRowTier(customer: Customer, picked: CustomerTier) {
    setPending((prev) => {
      const next = { ...prev };
      if (picked === customer.tier) {
        // Picked back to current → drop the pending edit.
        delete next[customer.id];
      } else {
        next[customer.id] = picked;
      }
      return next;
    });
    // Clear any stale per-row save status the moment the user edits again.
    setRowStatus((prev) => {
      if (!prev[customer.id]) return prev;
      const next = { ...prev };
      delete next[customer.id];
      return next;
    });
  }

  function applyBulkToVisible(picked: CustomerTier) {
    if (filtered.length === 0) return;
    setPending((prev) => {
      const next = { ...prev };
      for (const c of filtered) {
        if (picked === c.tier) {
          delete next[c.id];
        } else {
          next[c.id] = picked;
        }
      }
      return next;
    });
    setRowStatus({});
  }

  function clearPending() {
    setPending({});
    setRowStatus({});
  }

  async function handleSaveAll() {
    if (pendingCount === 0 || saving) return;
    setSaving(true);
    const now = new Date().toISOString();

    // Snapshot the pending edits — keys we'll iterate. New edits during
    // save are ignored (UI disables most interactions during save anyway).
    const entries = Object.entries(pending);

    // Mark everything queued so the user sees the batch front-loading.
    setRowStatus(() => {
      const seeded: Record<string, RowStatus> = {};
      for (const [id] of entries) seeded[id] = { kind: 'queued' };
      return seeded;
    });

    // Sequential window over the entries — at most SAVE_CONCURRENCY in flight.
    for (let i = 0; i < entries.length; i += SAVE_CONCURRENCY) {
      const batch = entries.slice(i, i + SAVE_CONCURRENCY);
      setRowStatus((prev) => {
        const next = { ...prev };
        for (const [id] of batch) next[id] = { kind: 'saving' };
        return next;
      });

      const results = await Promise.allSettled(
        batch.map(([id, tier]) =>
          updateCustomer(id, {
            tier,
            tierManualOverride: true,
            tierLastReviewed: now,
          }),
        ),
      );

      setRowStatus((prev) => {
        const next = { ...prev };
        for (let j = 0; j < batch.length; j++) {
          const entry = batch[j];
          const result = results[j];
          if (!entry || !result) continue;
          const [id] = entry;
          if (result.status === 'fulfilled') {
            next[id] = { kind: 'saved' };
          } else {
            const message =
              result.reason instanceof Error
                ? result.reason.message
                : String(result.reason);
            next[id] = { kind: 'error', message };
          }
        }
        return next;
      });
    }

    // Refresh from the wire so the "current tier" column is authoritative.
    // Pending entries whose save succeeded are now equal to the new
    // "current" — we clear the whole pending map and let the user re-pick
    // if a row errored (rowStatus surfaces the failure).
    try {
      const fresh = await listCustomers();
      setCustomers(fresh);
    } catch (e) {
      setError(`Failed to refresh after save: ${(e as Error).message}`);
    }
    setPending({});
    setSaving(false);
  }

  // Header counts for the "set all visible to" affordance.
  const tierCounts = useMemo(() => {
    const counts: Record<TierFilter, number> = {
      all: customers.length,
      vip: 0,
      good: 0,
      slow_pay: 0,
      bad: 0,
      new: 0,
    };
    for (const c of customers) counts[c.tier]++;
    return counts;
  }, [customers]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="text-xs text-text-mute hover:text-orange"
            >
              ← Customers
            </button>
          </div>
          <h1 className="text-2xl font-bold text-navy">Bulk tier reclassify</h1>
          <p className="text-sm text-text-mute">
            Quickly grade your customer rolodex without clicking into each card.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && !saving && (
            <button
              type="button"
              onClick={clearPending}
              className="text-xs text-text-mute hover:text-orange px-2"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleSaveAll()}
            disabled={pendingCount === 0 || saving}
            className={`px-3 py-1.5 text-sm rounded font-semibold transition-opacity ${
              pendingCount === 0 || saving
                ? 'bg-line-dark text-text-mute cursor-not-allowed'
                : 'bg-orange text-white hover:opacity-90'
            }`}
          >
            {saving
              ? `Saving ${pendingCount} change${pendingCount === 1 ? '' : 's'}…`
              : `Save ${pendingCount} change${pendingCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {/* Search + filter + bulk-apply */}
      <div className="bg-paper border border-line rounded p-3 mb-4">
        <div className="flex gap-2 items-center mb-3 flex-wrap">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by company name or code…"
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            disabled={saving}
          />
          <BulkApplyDropdown
            disabled={saving || filtered.length === 0}
            visibleCount={filtered.length}
            onPick={applyBulkToVisible}
          />
          <button
            type="button"
            onClick={refresh}
            disabled={loading || saving}
            className="text-xs text-text-mute hover:text-orange whitespace-nowrap px-2"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TIER_FILTERS.map((t) => {
            const active = tierFilter === t.key;
            const count = tierCounts[t.key];
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTierFilter(t.key)}
                disabled={saving}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  active
                    ? 'bg-orange text-white'
                    : 'bg-bg text-text-mute border border-line-dark hover:border-orange hover:text-orange'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t.label}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {loading && customers.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute">
          <div className="animate-pulse">Loading customers…</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && customers.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">📇</div>
          <div className="font-semibold text-navy mb-1">No customers yet</div>
          <p className="text-sm text-text-mute mb-4">
            Add some customers first, then come back here to grade them in bulk.
          </p>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            Back to Customers
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && customers.length > 0 && (
        <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
          <div className="text-sm">
            No customers match{' '}
            {searchQuery.trim() ? (
              <span className="font-mono">"{searchQuery}"</span>
            ) : (
              <span>this filter</span>
            )}
            .
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setTierFilter('all');
            }}
            className="text-xs text-orange font-semibold hover:underline mt-2"
          >
            Clear filters
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="bg-paper border border-line rounded overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-line-dark sticky top-0 z-10">
                <tr>
                  <th className="text-left font-semibold text-text-mute px-3 py-2 w-[24%]">
                    Company
                  </th>
                  <th className="text-left font-semibold text-text-mute px-3 py-2 w-[10%]">
                    Current
                  </th>
                  <th className="text-left font-semibold text-text-mute px-3 py-2 w-[32%]">
                    New tier
                  </th>
                  <th className="text-left font-semibold text-text-mute px-3 py-2 w-[22%]">
                    Notes
                  </th>
                  <th className="text-left font-semibold text-text-mute px-3 py-2 w-[12%]">
                    Last contact
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const picked = pending[c.id] ?? c.tier;
                  const modified = picked !== c.tier;
                  const status = rowStatus[c.id] ?? { kind: 'idle' };
                  const expanded = !!expandedNotes[c.id];
                  return (
                    <TierRow
                      key={c.id}
                      customer={c}
                      picked={picked}
                      modified={modified}
                      status={status}
                      expandedNotes={expanded}
                      onToggleNotes={() =>
                        setExpandedNotes((prev) => ({
                          ...prev,
                          [c.id]: !prev[c.id],
                        }))
                      }
                      onPick={(tier) => setRowTier(c, tier)}
                      disabled={saving}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-line bg-bg px-3 py-2 text-xs text-text-mute flex items-center justify-between">
            <span>
              Showing {filtered.length} of {customers.length} customer
              {customers.length === 1 ? '' : 's'}
            </span>
            {pendingCount > 0 && (
              <span className="text-orange font-semibold">
                {pendingCount} pending change{pendingCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface TierRowProps {
  customer: Customer;
  picked: CustomerTier;
  modified: boolean;
  status: RowStatus;
  expandedNotes: boolean;
  onToggleNotes: () => void;
  onPick: (tier: CustomerTier) => void;
  disabled: boolean;
}

function TierRow({
  customer,
  picked,
  modified,
  status,
  expandedNotes,
  onToggleNotes,
  onPick,
  disabled,
}: TierRowProps) {
  const noteText = latestNoteText(customer);
  const lastContact = fmtRelativeDays(customer.lastContactDate);

  const rowBg =
    status.kind === 'error'
      ? 'bg-orange/5'
      : status.kind === 'saved'
        ? 'bg-green/5'
        : modified
          ? 'bg-amber/5'
          : '';

  return (
    <tr className={`border-b border-line last:border-b-0 ${rowBg}`}>
      <td className="px-3 py-2 align-top">
        <div className="font-semibold text-navy truncate" title={customer.companyName}>
          {customer.companyName}
        </div>
        {customer.code && (
          <div className="text-[11px] font-mono text-text-mute">{customer.code}</div>
        )}
        {modified && (
          <div className="text-[11px] text-orange font-semibold mt-0.5">
            ● modified
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <TierBadge tier={customer.tier} size="md" />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-wrap gap-1">
          {TIER_PICKER_OPTIONS.map((opt) => {
            const active = picked === opt.key;
            const isCurrent = customer.tier === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onPick(opt.key)}
                disabled={disabled}
                title={isCurrent ? `${opt.label} (current)` : opt.label}
                className={`text-[11px] px-2 py-0.5 rounded-full font-semibold transition-colors border ${
                  active
                    ? 'bg-orange text-white border-orange'
                    : 'bg-paper text-text-mute border-line-dark hover:border-orange hover:text-orange'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <RowStatusPill status={status} />
      </td>
      <td className="px-3 py-2 align-top text-xs text-text-mute">
        {noteText ? (
          <button
            type="button"
            onClick={onToggleNotes}
            className="text-left hover:text-orange w-full"
          >
            <span className={expandedNotes ? '' : 'line-clamp-1 block'}>
              {noteText}
            </span>
            <span className="text-[10px] text-text-mute/70 italic">
              {expandedNotes ? 'click to collapse' : 'click to expand'}
            </span>
          </button>
        ) : (
          <span className="italic opacity-60">no notes</span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-xs text-text-mute whitespace-nowrap">
        {lastContact ?? <span className="italic opacity-60">never</span>}
      </td>
    </tr>
  );
}

function RowStatusPill({ status }: { status: RowStatus }) {
  if (status.kind === 'idle') return null;
  if (status.kind === 'queued') {
    return (
      <div className="text-[10px] text-text-mute mt-1 italic">queued…</div>
    );
  }
  if (status.kind === 'saving') {
    return (
      <div className="text-[10px] text-text-mute mt-1 italic animate-pulse">
        saving…
      </div>
    );
  }
  if (status.kind === 'saved') {
    return (
      <div className="text-[10px] text-green font-semibold mt-1">✓ saved</div>
    );
  }
  return (
    <div
      className="text-[10px] text-orange font-semibold mt-1"
      title={status.message}
    >
      ✗ failed
    </div>
  );
}

interface BulkApplyDropdownProps {
  disabled: boolean;
  visibleCount: number;
  onPick: (tier: CustomerTier) => void;
}

function BulkApplyDropdown({ disabled, visibleCount, onPick }: BulkApplyDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className={`text-xs px-3 py-1.5 rounded border font-semibold whitespace-nowrap ${
          disabled
            ? 'border-line-dark text-text-mute cursor-not-allowed opacity-50'
            : 'border-line-dark text-text hover:border-orange hover:text-orange'
        }`}
      >
        Set all visible to… ▾
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-paper border border-line-dark rounded shadow-lg z-20 min-w-[160px]">
          <div className="px-3 py-1.5 text-[11px] text-text-mute border-b border-line">
            Applies to {visibleCount} row{visibleCount === 1 ? '' : 's'}
          </div>
          {TIER_PICKER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => {
                onPick(opt.key);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-bg flex items-center gap-2"
            >
              <TierBadge tier={opt.key} size="sm" />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
